import { getLocale } from '@/locales/locales';
import { getApiBaseUrl } from './cookies';

const WEBAUTO_API_BASE = getApiBaseUrl();

class WaApiClient {
    /**
     * Performs an HTTP request to the specified URL with the given options.
     * @param {string} url - The URL to send the request to.
     * @param {RequestInit} options - The options for the fetch request.
     * @returns {Promise<any>} - The response from the server.
     */
    private async request(url: string, options: RequestInit): Promise<any> {
        const locale: string = getLocale();

        const headers: HeadersInit = {
            "Accept-Language": locale,
            ...options.headers,
        }

        const response: Response = await fetch(url, {
            credentials: "include",
            ...options,
            headers,
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Request to ${url} failed: status=${response.status}, statusText=${response.statusText}, response=${errorText}`);
        }

        const responseJson: any = await response.json();

        if (responseJson.status !== "success") {
            throw new Error(`Unsuccessful response from ${url}: ${JSON.stringify(responseJson)}`);
        }

        return responseJson;
    }

    /**
     * Performs a GET request to the specified URL.
     * @param {string} url - The URL to send the GET request to.
     * @returns {Promise<any>} - The response from the server.
     */
    async get(url: string): Promise<any> {
        return await this.request(url, { method: "GET" });
    }

    /**
     * Performs a POST request to the specified URL with the provided form data.
     * @param {string} url - The URL to send the POST request to.
     * @param {FormData} form - The form data to include in the POST request.
     * @returns {Promise<any>} - The response from the server.
     */
    async post(url: string, form?: FormData): Promise<any> {
        return await this.request(url, { method: "POST", body: form });
    }
}

export const startLesson = (params: { form: FormData; }) => {
    const { form } = params;

    if (!form) throw new Error('form missing in startLesson.');
    return api.post(`${WEBAUTO_API_BASE}/api/v1/self-study/courses/${form.get('courseId')}/lessons/${form.get("lessonId")}/start`, form);
};

export const getLesson = (params: { lessonId: string; courseId: string }) => {
    const { lessonId, courseId } = params;
    return api.get(`${WEBAUTO_API_BASE}/api/v1/self-study/courses/${courseId}/lessons/${lessonId}`);
};

export const getSections = (params: { completionId: string; }) => {
    const { completionId } = params;
    if (!completionId) throw new Error('completionId missing in getSections.');
    return api.get(`${WEBAUTO_API_BASE}/api/v1/self-study/lesson-progress/${completionId}/sections`);

};

export const getSectionData = (params: { url: string; }) => {
    const { url } = params;
    if (!url) throw new Error('url missing in getSectionData.');
    return api.get(`${WEBAUTO_API_BASE}${url}`);
};

export const setLessonComplete = (params: { form: FormData; }) => {
    const { form } = params;
    if (!form) throw new Error('form missing in setLessonComplete.');

    return api.post(`${WEBAUTO_API_BASE}/api/v1/self-study/lesson-progress/${form.get("completionId")}/complete`, form);
};

export const setSectionComplete = (params: { form: FormData; }) => {
    const { form } = params;
    if (!form) throw new Error('form missing in setSectionComplete.');
    return api.post(`${WEBAUTO_API_BASE}/api/v1/self-study/lesson-progress/${form.get("completionId")}/sections/${form.get("sectionNumber")}/complete`, form);

};

export const abortLesson = (params: { form: FormData; }) => {
    const { form } = params;

    if (!form) throw new Error('form missing in abortLesson.');
    return api.post(`${WEBAUTO_API_BASE}/api/v1/self-study/lesson-progress/${form.get("completionId")}/abort`, form);

};

export const getQuestion = (params: { questionId: string, completionId: string; }) => {
    const { questionId, completionId } = params;

    if (!questionId) throw new Error('questionId missing in getQuestion.');

    if (!completionId) throw new Error('completionId missing in getQuestion.');

    return api.get(`${WEBAUTO_API_BASE}/api/v1/self-study/lesson-progress/${completionId}/questions/${questionId}`);
};

export const saveAnswer = (params: { form: FormData; }) => {
    const { form } = params;
    if (!form) throw new Error('form missing in saveAnswer.');
    return api.post(`${WEBAUTO_API_BASE}/api/v1/self-study/lesson-progress/${form.get("completionId")}/questions/${form.get("questionId")}/answer`, form);
};

export const getQuestionFormSummary = (params: { questionFormId: string; completionId: string; }) => {
    const { completionId, questionFormId } = params;
    if (!completionId) throw new Error('completionId missing in getQuestionFormSummary.');
    if (!questionFormId) throw new Error('questionFormId missing in getQuestionFormSummary.');
    return api.get(`${WEBAUTO_API_BASE}/api/v1/self-study/lesson-progress/${completionId}/question-form-summary/${questionFormId}`);
};

export const getBookPart = (params: { pageId: string; bookId: string; completionId: string; }) => {
    const { bookId, pageId, completionId } = params;
    if (!bookId) throw new Error('bookId missing in getBookPart.');
    if (!pageId) throw new Error('pageId missing in getBookPart.');
    return api.get(`${WEBAUTO_API_BASE}/api/v1/self-study/lesson-progress/${completionId}/books/${bookId}/pages/${pageId}`);
};

export const getLessonGroups = (params: { courseId: string; drivingLicense: string; }) => {
    const { courseId } = params;
    const { drivingLicense } = params;

    return api.get(`${WEBAUTO_API_BASE}/api/v1/self-study/courses/${courseId}/subject-groups/${drivingLicense}`);
};

export const getGroupData = (params: { id: string; courseId: string; drivingLicense: string; }) => {
    const { id } = params;
    const { courseId } = params;
    const { drivingLicense } = params;

    if (!id) throw new Error('id missing in getGroupData.');
    return api.get(`${WEBAUTO_API_BASE}/api/v1/self-study/courses/${courseId}/subject-groups/${drivingLicense}/${id}`);
};

export const getLessonSummary = (params: { completionId: string; }) => {
    const { completionId } = params;
    if (!completionId) throw new Error('completionId missing in getLessonSummary.');
    return api.get(`${WEBAUTO_API_BASE}/api/v1/self-study/lesson-progress/${completionId}/completion-summary`);
};

export const textToSpeech = (params: { form: FormData; }) => {
    const { form } = params;
    if (!form) throw new Error('form missing in textToSpeech.');
    return api.post(`${WEBAUTO_API_BASE}/api/v1/text-to-speech`, form);
};

export const getMaterialBook = (params: { bookId: string; }) => {
    const { bookId } = params;
    if (!bookId) throw new Error('bookId missing in getMaterialBook.');
    return api.get(`${WEBAUTO_API_BASE}/api/v1/self-study/books/${bookId}`);
};

export const getMaterialBookPage = (params: { pageId: string; bookId: string; }) => {
    const { bookId, pageId } = params;
    if (!bookId) throw new Error('bookId missing in getMaterialBookPage.');
    if (!pageId) throw new Error('pageId missing in getMaterialBookPage.');
    return api.get(`${WEBAUTO_API_BASE}/api/v1/self-study/books/${bookId}/pages/${pageId}`);
};

export const getPortalConfiguration = (params: { courseId: string; drivingLicense: string; }) => {
    const { courseId } = params;
    const { drivingLicense } = params;

    return api.get(`${WEBAUTO_API_BASE}/api/v1/self-study/courses/${courseId}/portal-configuration/${drivingLicense}`);
};

const api = new WaApiClient();
